import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  CameraIcon, 
  UserGroupIcon, 
  BriefcaseIcon, 
  SparklesIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import SEOHead from '../components/common/SEOHead';
import AppComingSoonSection from '../components/landing/AppComingSoonSection';
import PublicNavbar from '../components/common/PublicNavbar';
import BrandLogo from '../components/common/BrandLogo';
import { useAppSelector } from '../hooks/redux';

interface PlatformStats {
  total_creators: number;
  total_models: number;
  total_hirers: number;
  total_collaborations: number;
  total_opportunities: number;
}

const MainLandingPage: React.FC = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.95]);

  const [activeTab, setActiveTab] = useState<'clients' | 'creators'>('clients');
  const [stats, setStats] = useState<PlatformStats>({
    total_creators: 0,
    total_models: 0,
    total_hirers: 0,
    total_collaborations: 0,
    total_opportunities: 0
  });
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/stats/public');
        if (response.data.success) {
          setStats(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  const fadeInUp: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const AnimatedCounter = ({ value, suffix = '' }: { value: number; suffix?: string }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }, [value]);
    
    return <span>{count.toLocaleString()}{suffix}</span>;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-sans selection:bg-black selection:text-white dark:selection:bg-white dark:selection:text-black overflow-x-hidden">
      <SEOHead
        title="ClickCollab - Find & Hire Photographers & Models Near You in India"
        description="Connect with professional photographers, videographers, and models across India. Book wedding photographers, fashion models, event photographers, and creative professionals. Browse portfolios, check ratings, and collaborate easily. India's #1 creative talent platform."
        keywords={[
          'photographer near me',
          'hire photographer India',
          'find photographer',
          'professional photographer',
          'book photographer online',
          'model near me',
          'hire model India',
          'wedding photographer',
          'wedding photography India',
          'candid wedding photographer',
          'pre-wedding shoot',
          'destination wedding photographer',
          'wedding videographer',
          'portrait photographer',
          'fashion photographer',
          'fashion model',
          'headshot photographer',
          'event photographer',
          'birthday photographer',
          'product photographer',
          'photography platform India',
          'model booking platform',
          'creative collaboration',
          'photographer marketplace India',
        ]}
      />
      
      <PublicNavbar />

      <section className="relative h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100 via-white to-white dark:from-gray-900 dark:via-gray-900 dark:to-black -z-10" />
        
        <motion.div 
          style={{ opacity, scale }}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-6">
              The #1 Platform for Creative Professionals
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-4xl sm:text-6xl md:text-8xl font-bold tracking-tighter mb-4 md:mb-8 bg-clip-text text-transparent bg-gradient-to-b from-gray-900 to-gray-600 dark:from-white dark:to-gray-400"
          >
            Find. Book.<br />Create.
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-base md:text-2xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-8 md:mb-12 leading-relaxed px-2"
          >
            The easiest way to find photographers, models, and creative talent. 
            Search by budget, location, and style. Book instantly.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 px-4"
          >
            {user ? (
              <Link 
                to="/dashboard"
                className="w-full sm:w-auto group relative px-6 py-3 md:px-8 md:py-4 bg-black dark:bg-white text-white dark:text-black text-base md:text-lg font-medium rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Go to Dashboard
                  <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </Link>
            ) : (
              <Link 
                to="/register"
                className="w-full sm:w-auto group relative px-6 py-3 md:px-8 md:py-4 bg-black dark:bg-white text-white dark:text-black text-base md:text-lg font-medium rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Get Started Free
                  <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </Link>
            )}
            <Link 
              to="/explore"
              className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base md:text-lg font-medium rounded-full border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              Explore Talent
            </Link>
          </motion.div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
        >
          <span className="text-xs text-gray-400 mb-2">Scroll to explore</span>
          <div className="w-px h-12 bg-gradient-to-b from-gray-300 to-transparent" />
        </motion.div>
      </section>
{/* 
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter value={stats.total_creators} suffix="+" />
              </div>
              <div className="text-gray-400 text-sm">Photographers</div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter value={stats.total_models} suffix="+" />
              </div>
              <div className="text-gray-400 text-sm">Models</div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter value={stats.total_hirers} suffix="+" />
              </div>
              <div className="text-gray-400 text-sm">Clients</div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter value={stats.total_collaborations} suffix="+" />
              </div>
              <div className="text-gray-400 text-sm">Collaborations</div>
            </motion.div>
            <motion.div variants={fadeInUp} className="col-span-2 md:col-span-1">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter value={stats.total_opportunities} suffix="+" />
              </div>
              <div className="text-gray-400 text-sm">Opportunities</div>
            </motion.div>
          </motion.div>
        </div>
      </section> */}

      <section className="py-20 md:py-32 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">One Platform, Infinite Possibilities</h2>
            <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-8">
              Whether you're looking for talent or showcasing your skills
            </p>
            {/* <div className="inline-flex p-1 bg-white dark:bg-gray-800 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('clients')}
                className={`px-6 md:px-8 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'clients' 
                    ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                I'm Looking for Talent
              </button>
              <button
                onClick={() => setActiveTab('creators')}
                className={`px-6 md:px-8 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'creators' 
                    ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                I'm the Talent
              </button>
            </div> */}
              {/* Features - Alternating Layout */}
          <div className="flex flex-col gap-12 md:gap-24 lg:gap-32 mt-8 md:mt-20">
            
            {/* Feature 1: CRM */}
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 lg:gap-16 w-full">
              {/* Text Side (Left) */}
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center">
                  <UserGroupIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  CRM & Client Management
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Track leads, manage client communications, and coordinate photography projects or creative gigs from one dashboard. Build stronger relationships with less effort.
                </p>
                </motion.div>
              </div>
              
              {/* Image/Mockup Side (Right) */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3]"
              >
                {/* Background glowing orb */}
                <div className="absolute inset-0 bg-blue-100 dark:bg-blue-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Floating Client Cards */}
                <div className="relative z-10 w-full max-w-[260px] md:max-w-md h-[180px] md:h-80 perspective-1000 transform scale-[0.65] md:scale-100 origin-top">
                  {/* Back Card */}
                  <motion.div 
                    initial={{ y: 20, rotateX: 10, scale: 0.9 }}
                    animate={{ y: [20, 10, 20], rotateX: [10, 5, 10] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 right-4 w-64 h-24 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-slate-700 shadow-xl p-4 flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex-shrink-0 flex items-center justify-center">
                       <UserGroupIcon className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="space-y-2 w-full">
                      <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-3/4" />
                      <div className="h-2 bg-blue-500/30 rounded w-1/2" />
                    </div>
                  </motion.div>

                  {/* Front Card */}
                  <motion.div 
                    initial={{ y: 0, rotateX: 5 }}
                    animate={{ y: [0, -10, 0], rotateX: [5, 0, 5] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute top-12 left-0 w-72 h-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl border-t-4 border-t-indigo-500 border-x border-b border-slate-100 dark:border-slate-800 shadow-[0_20px_50px_rgba(79,70,229,0.15)] dark:shadow-[0_20px_50px_rgba(79,70,229,0.3)] p-5 flex flex-col justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-teal-600 shadow-inner overflow-hidden rounded-full flex items-center justify-center text-white font-bold text-lg">
                        Nykaa
                      </div>
                      <div>
                        <div className="text-base font-bold text-slate-900 dark:text-white mb-1">Brand Shoot</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Campaign 2026</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <div className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 text-[10px] uppercase tracking-wider font-bold rounded-full flex items-center gap-1.5 shadow-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span> IN PROGRESS
                      </div>
                      <div className="text-xs font-medium text-slate-400">Due in 2 days</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Feature 2: Invoicing (Reversed) */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-6 md:gap-10 lg:gap-16 w-full">
              {/* Text Side (Right) */}
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center">
                  <DocumentTextIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Invoicing & Contracts
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Create professional invoices, process secure payments, and sign digital booking contracts or release forms online. Get paid faster and protect your work.
                </p>
                </motion.div>
              </div>
              
              {/* Image/Mockup Side (Left) */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3] p-0 md:p-8"
              >
                <div className="absolute inset-0 bg-emerald-100 dark:bg-emerald-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Digital Invoice */}
                <motion.div 
                  initial={{ rotateY: -5 }}
                  animate={{ rotateY: [-5, 5, -5] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                  className="relative z-10 w-full max-w-[280px] md:max-w-sm lg:max-w-md bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-[0_30px_60px_rgba(16,185,129,0.15)] overflow-hidden border border-slate-100 dark:border-slate-800 transform scale-[0.8] md:scale-100 origin-top"
                >
                   {/* Receipt jagged top */}
                   <div className="h-3 w-full bg-[radial-gradient(circle,transparent_4px,#fff_5px)] dark:bg-[radial-gradient(circle,transparent_4px,#0f172a_5px)] bg-[length:12px_12px] bg-repeat-x -mt-1" />
                   
                   <div className="p-8">
                      <div className="flex justify-between items-start mb-8 border-b border-slate-100 dark:border-slate-800 pb-6">
                        <div>
                          <div className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mb-1">Receipt</div>
                          <div className="text-2xl font-black bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent tracking-tight">INV-2049</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mb-1">Amount</div>
                          <div className="text-xl text-slate-900 dark:text-white font-black">₹1,25,000</div>
                        </div>
                      </div>
                      
                      <div className="space-y-5 mb-8">
                        <div className="flex justify-between items-center text-sm">
                          <div className="text-slate-500 dark:text-slate-400 font-medium">Commercial License</div>
                          <div className="font-bold text-slate-900 dark:text-slate-200">₹75,000</div>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <div className="text-slate-500 dark:text-slate-400 font-medium">Art Direction</div>
                          <div className="font-bold text-slate-900 dark:text-slate-200">₹50,000</div>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-slate-100 dark:border-slate-800 border-dashed">
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.6, type: "spring" }}
                          className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/50 dark:border-emerald-800/50 rounded-xl p-4 flex items-center gap-3 text-emerald-700 dark:text-emerald-400 font-bold shadow-sm"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-800/50 flex items-center justify-center">
                            <CheckCircleIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 flex justify-between items-center">
                            <span className="text-sm">Paid in Full</span>
                            <span className="text-[10px] uppercase tracking-wider font-extrabold opacity-70">Today</span>
                          </div>
                        </motion.div>
                      </div>
                   </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Feature 3: Opportunities */}
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 lg:gap-16 w-full">
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-2xl flex items-center justify-center">
                  <BriefcaseIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Opportunities Board
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Browse casting calls for models, photography job postings, and seamlessly apply for paid or TFP collaborations to fill your schedule.
                </p>
                </motion.div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3]"
              >
                <div className="absolute inset-0 bg-teal-100 dark:bg-teal-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Job Cards */}
                <div className="relative z-10 w-full max-w-sm lg:max-w-md flex flex-col gap-3 md:gap-4 transform scale-85 md:scale-100 origin-top">
                  <motion.div 
                    whileHover={{ scale: 1.02, x: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-6 rounded-2xl shadow-[0_15px_40px_rgba(168,85,247,0.15)] border border-slate-100 dark:border-slate-800 overflow-hidden relative cursor-pointer group"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-teal-500 to-fuchsia-500" />
                    <div className="flex justify-between items-start mb-6 pl-2">
                      <div className="space-y-1.5">
                        <div className="text-base font-bold text-slate-900 dark:text-white tracking-tight">Global Fall Campaign</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <MapPinIcon className="w-3.5 h-3.5 text-fuchsia-500" /> Mumbai, MH
                        </div>
                      </div>
                      <div className="bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-[10px] font-bold px-3 py-1.5 rounded-full tracking-wide shadow-sm">
                        ₹25,000 / DAY
                      </div>
                    </div>
                    <div className="flex gap-2 pl-2">
                      <div className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold px-2.5 py-1 rounded w-fit">Commercial</div>
                      <div className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold px-2.5 py-1 rounded w-fit">2 Days</div>
                    </div>
                  </motion.div>

                  <motion.div 
                    whileHover={{ scale: 1.02, x: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-md p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800/50 relative overflow-hidden cursor-pointer"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-blue-400 to-cyan-400" />
                    <div className="flex justify-between items-start mb-6 pl-2">
                      <div className="space-y-1.5">
                        <div className="text-base font-bold text-slate-700 dark:text-slate-200 tracking-tight">Editorial Feature</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <MapPinIcon className="w-3.5 h-3.5 text-cyan-500" /> Bengaluru, KA
                        </div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 text-[10px] font-bold px-3 py-1.5 rounded-full tracking-wide">
                        EDITORIAL EXPOSURE
                      </div>
                    </div>
                    <div className="flex gap-2 pl-2">
                      <div className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-[10px] font-bold px-2.5 py-1 rounded w-fit">Fashion</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Feature 4: Portfolios (Reversed) */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-6 md:gap-10 lg:gap-16 w-full">
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-2xl flex items-center justify-center">
                  <CameraIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Visual Portfolios
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Showcase your modeling composite cards and photography work in stunning, high-resolution galleries. Let your art speak for itself in full bleed layouts.
                </p>
                </motion.div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3]"
              >
                <div className="absolute inset-0 bg-pink-100 dark:bg-pink-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Masonry Grid */}
                <div className="relative z-10 w-[260px] md:w-full md:max-w-sm lg:max-w-md grid grid-cols-2 gap-3 md:gap-5 transform scale-90 md:scale-100 -rotate-3 hover:rotate-0 transition-all duration-700 mx-auto origin-center">
                  <div className="space-y-4">
                    <motion.div 
                      whileHover={{ scale: 1.03 }}
                      className="w-full h-24 md:h-44 bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl shadow-xl border-2 border-white/80 dark:border-slate-800 object-cover relative overflow-hidden group"
                    >
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent" />
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.03 }}
                      className="w-full h-16 md:h-32 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl shadow-xl border-2 border-white/80 dark:border-slate-800 object-cover relative overflow-hidden group"
                    >
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent" />
                    </motion.div>
                  </div>
                  <div className="space-y-4 pt-10">
                    <motion.div 
                      whileHover={{ scale: 1.03 }}
                      className="w-full h-20 md:h-32 bg-gradient-to-br from-teal-400 to-indigo-500 rounded-2xl shadow-xl border-2 border-white/80 dark:border-slate-800 object-cover relative overflow-hidden group"
                    >
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent" />
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.03 }}
                      className="w-full h-24 md:h-48 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-xl border-2 border-white/80 dark:border-slate-800 object-cover relative overflow-hidden group"
                    >
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Feature 5: Messaging */}
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 lg:gap-16 w-full">
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-2xl flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Integrated Messaging
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Communicate securely and directly with clients, agencies, and creative collaborators in real-time. Share mood boards and negotiate terms without losing context.
                </p>
                </motion.div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3] p-0 md:p-6"
              >
                <div className="absolute inset-0 bg-orange-100 dark:bg-orange-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Chat Interface */}
                <div className="relative z-10 w-full max-w-[280px] md:max-w-sm lg:max-w-md bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-[0_25px_50px_rgba(249,115,22,0.15)] dark:shadow-[0_25px_50px_rgba(249,115,22,0.25)] border border-slate-100 dark:border-slate-800 p-3 md:p-5 pb-4 md:pb-6 flex flex-col gap-2 md:gap-5 overflow-hidden transform scale-[0.8] md:scale-100 origin-top mx-auto">
                  
                  {/* Sender Bubble */}
                  <motion.div 
                    initial={{ x: 30, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="self-end bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-2xl rounded-tr-sm p-4 max-w-[85%] shadow-md"
                  >
                    <div className="text-sm font-medium leading-relaxed">The moodboard looks incredible. When can we lock in the dates?</div>
                  </motion.div>

                  {/* Receiver Bubble */}
                  <motion.div 
                    initial={{ x: -30, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="self-start bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-tl-sm p-4 max-w-[85%] flex items-end gap-3 shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex-shrink-0 flex items-center justify-center shadow-inner">
                       <UserGroupIcon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">I'm available next Tuesday or Thursday. Let me know what works!</div>
                    </div>
                  </motion.div>

                  {/* Input Mock */}
                  <div className="mt-3 flex gap-2 items-center">
                    <div className="flex-1 h-12 bg-slate-50 dark:bg-slate-800/80 rounded-full border border-slate-200 dark:border-slate-700 px-5 flex items-center shadow-inner">
                      <div className="text-sm text-slate-400">Type a message...</div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 flex-shrink-0 flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform cursor-pointer">
                      <ArrowRightIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Feature 6: Discovery (Reversed) */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-6 md:gap-10 lg:gap-16 w-full">
              <div className="flex-[0.9] flex flex-col gap-4 md:gap-6 contents md:flex">
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className="order-1 md:order-none space-y-3 md:space-y-6 w-full flex flex-col items-center md:items-start text-center md:text-left"
                >

                <div className="w-16 h-16 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-2xl flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Smart Discovery
                </h3>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="order-3 md:order-none w-full text-center md:text-left"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                  Get discovered by top brands, or effortlessly find local portrait models and photographers using advanced semantic search filters and location tags.
                </p>
                </motion.div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="flex-[1.2] order-2 md:order-none relative w-full flex items-center justify-center min-h-[140px] md:aspect-[4/3]"
              >
                <div className="absolute inset-0 bg-cyan-100 dark:bg-cyan-900/20 rounded-full blur-3xl opacity-50 transform scale-75 animate-pulse" />
                
                {/* CSS Mockup: Search Interface */}
                <div className="relative z-10 w-full max-w-[280px] md:max-w-sm lg:max-w-md flex flex-col items-center transform scale-[0.85] md:scale-100 origin-top mx-auto">
                  {/* Search Bar */}
                  <motion.div 
                    initial={{ y: -20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    className="w-full h-16 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-full shadow-[0_20px_50px_rgba(6,182,212,0.15)] dark:shadow-[0_20px_50px_rgba(6,182,212,0.25)] border border-slate-100 dark:border-slate-800 flex items-center px-6 gap-4 mb-10 z-20"
                  >
                    <MagnifyingGlassIcon className="w-6 h-6 text-cyan-500" />
                    <div className="flex-1 flex items-center justify-between">
                       <span className="text-slate-900 dark:text-white font-bold text-sm">Portrait Models</span>
                       <div className="flex items-center gap-2">
                         <span className="w-px h-5 bg-slate-200 dark:bg-slate-700"></span>
                         <span className="text-cyan-600 dark:text-cyan-400 font-medium text-sm">Mumbai</span>
                       </div>
                    </div>
                  </motion.div>

                  {/* Search Results Avatars */}
                  <div className="flex justify-center -space-x-4">
                    {[1, 2, 3, 4].map((i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1, type: "spring" }}
                        className="w-16 h-16 rounded-full border-4 border-white dark:border-slate-900 bg-gradient-to-tr from-cyan-400 to-blue-500 shadow-lg relative overflow-hidden flex items-center justify-center transform hover:translate-y-[-4px] transition-transform cursor-pointer"
                      >
                         <svg className="w-7 h-7 text-white/80" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                      </motion.div>
                    ))}
                    <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, type: "spring" }}
                        className="w-16 h-16 rounded-full border-4 border-white dark:border-slate-900 bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 font-extrabold text-sm z-10 cursor-pointer"
                      >
                         +42
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {activeTab === 'clients' ? (
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <MagnifyingGlassIcon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Smart Search</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Find the perfect photographer or model in seconds. Filter by style, specialization, 
                    ratings, and availability.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                    <CurrencyDollarIcon className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Budget Friendly</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Search by your budget. From TFP collaborations to premium packages, 
                    find talent that fits your price range.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                    <MapPinIcon className="w-7 h-7 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Location Based</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Discover talented professionals near you. Our location search 
                    helps you find local talent for your projects.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                    <StarIcon className="w-7 h-7 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Get Discovered</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Showcase your portfolio to thousands of potential clients. 
                    Get found by brands, agencies, and individuals looking for your skills.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                    <CurrencyDollarIcon className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Earn More</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Set your own rates and sell service packages directly. 
                    No middlemen, instant bookings, and secure payments.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                    <BriefcaseIcon className="w-7 h-7 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Find Opportunities</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Browse casting calls, job postings, and collaboration requests. 
                    Apply with one click and grow your portfolio.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section id="features" className="py-12 md:py-32 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-500 dark:text-gray-400">All-in-one platform for creative professionals</p>
          </div>
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-12"
          >
            <motion.div variants={fadeInUp} className="group">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-black dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-colors duration-300">
                <CameraIcon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Visual Portfolios</h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Showcase your work in stunning high-resolution grids. Designed specifically for visual artists to make every pixel count.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="group">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-black dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-colors duration-300">
                <UserGroupIcon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Collaboration Hub</h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Build your dream team. Post casting calls, manage applications, and coordinate shoots with integrated project tools.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="group">
              <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-black dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-black transition-colors duration-300">
                <BriefcaseIcon className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Service Packages</h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Turn your passion into profit. Define your services, set your rates, and get booked directly with zero friction.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-32 bg-black text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 to-black opacity-50" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <SparklesIcon className="w-12 h-12 text-yellow-400 mx-auto mb-8" />
          <h2 className="text-4xl md:text-7xl font-bold mb-8 tracking-tight">Ready to create?</h2>
          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            Join our growing community of photographers, models, and creative professionals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <Link 
                to="/dashboard"
                className="w-full sm:w-auto px-10 py-5 bg-white text-black text-xl font-bold rounded-full hover:bg-gray-200 transition-colors transform hover:scale-105 duration-200"
              >
                Go to Dashboard
              </Link>
            ) : (
              <Link 
                to="/register"
                className="w-full sm:w-auto px-10 py-5 bg-white text-black text-xl font-bold rounded-full hover:bg-gray-200 transition-colors transform hover:scale-105 duration-200"
              >
                Get Started Free
              </Link>
            )}
            <Link 
              to="/contact"
              className="w-full sm:w-auto px-10 py-5 bg-transparent text-white text-xl font-bold rounded-full border-2 border-white/30 hover:border-white transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <AppComingSoonSection />

      <footer className="bg-white dark:bg-gray-900 py-12 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <BrandLogo textSize="text-lg" />
          </div>
          <div className="flex space-x-8 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/explore" className="hover:text-black dark:hover:text-white">Explore</Link>
            <Link to="/opportunities" className="hover:text-black dark:hover:text-white">Opportunities</Link>
            <Link to="/contact" className="hover:text-black dark:hover:text-white">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-black dark:hover:text-white">Privacy Policy</Link>
            <Link to="/login" className="hover:text-black dark:hover:text-white">Login</Link>
          </div>
          <div className="mt-4 md:mt-0 text-sm text-gray-400">
            © {new Date().getFullYear()} ClickCollab. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLandingPage;
